import React, { Component } from "react";
import { Link } from "react-router-dom";
import DataTable from "react-data-table-component";
import Loader from "react-loaders";
import { AiOutlineDownload, AiOutlineSearch } from "react-icons/ai";
import { FiChevronDown } from "react-icons/fi";
import { ToastContainer } from "react-toastify";
import { saveAs } from "file-saver";
import axiosInstance from "../configuration/Request";
import { showErrorToast } from "../Components/Toaster";
import RoundedPagination from "../ReusableComponent/RoundedPagination";
import { dataTableCustomStyles, formatStat, estimateColor } from "../ReusableComponent/biomarkerTableStyles";
import backlogo from "../../assets/images/svg/Back.svg";
import ProjectionGeneDetailPanel from "./ProjectionGeneDetailPanel";
import { surgeryRadiationLabel } from "./surgeryRadiationLabel";

const tabs = [
  { value: "prognostic", label: "Prognostic projection" },
  { value: "treatment", label: "Treatment projection" },
  { value: "cox", label: "Cox / survival" },
];
const labels = {
  gene: "Gene name", treatment: "Treatment", SurgRad: "Surgery / radiation",
  estimate: "Estimate", stdError: "Standard error", statistic: "Statistic",
  pValue: "P-value", matchedStrata: "Matched strata", hazardRatio: "Hazard ratio",
  confidenceInterval: "95% CI", logHazardRatio: "Log HR", z: "Z statistic",
  patientCount: "Patients", eventCount: "Events",
};
const coxKeys = ["gene", "hazardRatio", "pValue", "confidenceInterval", "logHazardRatio", "stdError", "z", "patientCount", "eventCount"];
const confidenceInterval = (row) => `${formatStat(row.ciLower)} – ${formatStat(row.ciUpper)}`;
const baseKeys = ["gene", "estimate", "stdError", "statistic", "pValue", "matchedStrata"];
const displayValue = (value) => value == null ? "—" : typeof value === "object" ? JSON.stringify(value) : String(value);
const filterValues = (rows, key) => [...new Set(rows
  .map((row) => row[key])
  .filter((value) => value != null && String(value).trim() !== "")
  .map(String))].sort((a, b) => a.localeCompare(b));

export default class ViewPopulationProjection extends Component {
  constructor(props) {
    super(props);
    const projection = props.location?.state?.projection || {};
    this.state = {
      projection, resultType: "prognostic", rows: [], loading: true, error: "",
      counts: projection.result_counts || {}, expiresAt: projection.expires_at,
      gene: "", appliedGene: "", treatment: "", SurgRad: "",
      treatmentOptions: [], now: Date.now(),
      genePanel: null,
      expired: false,
    };
  }

  componentDidMount() {
    this.scheduleExpiry();
    this.fetchResults();
    if (!this.props.location?.state?.projection) this.fetchMetadata();
    this.expiryTimer = setInterval(() => this.setState({ now: Date.now() }), 60000);
    window.addEventListener("focus", this.scheduleExpiry);
    document.addEventListener("visibilitychange", this.scheduleExpiry);
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.expiresAt !== this.state.expiresAt) this.scheduleExpiry();
  }

  isExpired = () => this.state.expired || Date.parse(this.state.expiresAt) <= Date.now();

  expireAnalysis = () => {
    clearTimeout(this.expiryDeadline);
    this.resultsController?.abort();
    this.plotsController?.abort();
    this.setState({ expired: true, now: Date.now(), rows: [], genePanel: null, loading: false, error: "" });
  };

  scheduleExpiry = () => {
    clearTimeout(this.expiryDeadline);
    const remaining = Date.parse(this.state.expiresAt) - Date.now();
    if (!Number.isFinite(remaining)) return;
    if (remaining <= 0) {
      if (!this.state.expired) this.expireAnalysis();
      return;
    }
    // Schedule at the expiry instant instead of waiting for the countdown refresh.
    this.expiryDeadline = setTimeout(this.scheduleExpiry, Math.min(remaining, 2147483647));
  };

  componentWillUnmount() {
    this.resultsController?.abort();
    this.metadataController?.abort();
    this.plotsController?.abort();
    clearInterval(this.expiryTimer);
    clearTimeout(this.expiryDeadline);
    window.removeEventListener("focus", this.scheduleExpiry);
    document.removeEventListener("visibilitychange", this.scheduleExpiry);
  }

  fetchMetadata = async () => {
    const controller = new AbortController();
    this.metadataController = controller;
    try {
      const response = await axiosInstance.get(`${process.env.REACT_APP_BASE_URL}api/population-projections`, {
        params: { "filters[analysis_id][$eq]": this.props.match.params.analysisId, "pagination[pageSize]": 1 },
        signal: controller.signal,
      });
      if (controller.signal.aborted) return;
      const projection = response.data?.data?.[0]?.attributes || {};
      this.setState((state) => ({
        projection, counts: { ...projection.result_counts, ...state.counts },
        expiresAt: state.expiresAt || projection.expires_at,
      }));
    } catch (error) {
      if (!controller.signal.aborted) showErrorToast("Unable to load analysis details.");
    }
  };

  fetchResults = async () => {
    if (this.isExpired()) {
      this.expireAnalysis();
      return;
    }
    this.closeGenePanel();
    this.resultsController?.abort();
    const controller = new AbortController();
    this.resultsController = controller;
    const { resultType, appliedGene } = this.state;
    this.setState({ loading: true, error: "", rows: [] });
    const params = {
      analysisId: this.props.match.params.analysisId, resultType, limit: 100,
      ...(appliedGene ? { gene: appliedGene } : {}),
    };
    try {
      let cursor;
      let payload;
      const seenCursors = new Set();
      const rows = [];
      do {
        const response = await axiosInstance.get(`${process.env.REACT_APP_BASE_URL}api/projection-custom/projection_result`, {
          params: { ...params, ...(cursor ? { cursor } : {}) }, signal: controller.signal,
        });
        if (controller.signal.aborted) return;
        payload = response.data;
        if (payload?.expiresAt) {
          this.setState({ expiresAt: payload.expiresAt });
          if (Date.parse(payload.expiresAt) <= Date.now()) {
            this.expireAnalysis();
            return;
          }
        }
        if (this.isExpired()) { this.expireAnalysis(); return; }
        if (!Array.isArray(payload?.results)) throw new Error("Invalid projection results response");
        rows.push(...payload.results);
        cursor = payload.nextCursor;
        if (cursor && seenCursors.has(cursor)) throw new Error("Repeated results cursor");
        if (cursor) seenCursors.add(cursor);
      } while (cursor);
      this.setState((state) => {
        const treatmentOptions = filterValues(rows, "treatment");
        const treatment = treatmentOptions.includes(state.treatment) ? state.treatment : "";
        const surgeryOptions = filterValues(rows.filter((row) => !treatment || String(row.treatment) === treatment), "SurgRad");
        return {
          rows, loading: false, expiresAt: payload.expiresAt || state.expiresAt,
          counts: !appliedGene
            ? { ...state.counts, [resultType]: payload.total ?? rows.length } : state.counts,
          ...(resultType === "treatment" ? {
            treatmentOptions, treatment,
            SurgRad: surgeryOptions.includes(state.SurgRad) ? state.SurgRad : "",
          } : {}),
        };
      });
    } catch (error) {
      if (controller.signal.aborted) return;
      this.setState({ loading: false, rows: [], error: "Unable to load projection results. Please try again." });
      showErrorToast("Error while fetching projection results");
    }
  };

  handleTabChange = (resultType) => {
    if (resultType === this.state.resultType) return;
    this.setState({ resultType, gene: "", appliedGene: "", treatment: "", SurgRad: "" }, this.fetchResults);
  };

  handleSearch = (event) => {
    event.preventDefault();
    this.setState({ appliedGene: this.state.gene.trim() }, this.fetchResults);
  };

  getSurgeryOptions = (treatment = this.state.treatment) => filterValues(
    this.state.rows.filter((row) => !treatment || String(row.treatment) === treatment), "SurgRad"
  );

  handleTreatmentChange = (treatment) => {
    this.closeGenePanel();
    this.setState((state) => ({
      treatment,
      SurgRad: this.getSurgeryOptions(treatment).includes(state.SurgRad) ? state.SurgRad : "",
    }));
  };

  handleSurgeryChange = (SurgRad) => {
    this.closeGenePanel();
    this.setState({ SurgRad });
  };

  getFilteredRows = () => {
    const { rows, resultType, treatment, SurgRad } = this.state;
    if (resultType !== "treatment") return rows;
    return rows.filter((row) => (!treatment || String(row.treatment) === treatment) &&
      (!SurgRad || String(row.SurgRad) === SurgRad));
  };

  handleGeneClick = async (row) => {
    if (this.isExpired()) { this.expireAnalysis(); return; }
    if (!row.gene) return;
    this.plotsController?.abort();
    const controller = new AbortController();
    this.plotsController = controller;
    const { resultType } = this.state;
    const panel = { row, resultType, loading: true, data: null, error: "" };
    this.setState({ genePanel: panel });
    const payload = {
      analysisId: this.props.match.params.analysisId,
      resultType,
      gene: row.gene,
      ...(resultType === "treatment" ? { treatment: row.treatment, SurgRad: row.SurgRad } : {}),
    };

    try {
      const response = await axiosInstance.post(
        `${process.env.REACT_APP_BASE_URL}api/projection-custom/projection_plots`,
        payload,
        { signal: controller.signal }
      );
      if (controller.signal.aborted) return;
      this.setState({ genePanel: { ...panel, loading: false, data: response.data } });
    } catch (error) {
      if (controller.signal.aborted) return;
      this.setState({ genePanel: { ...panel, loading: false, error: "Unable to load this plot. Please try again." } });
      showErrorToast("Error while fetching projection plots. Please try again.");
    }
  };

  closeGenePanel = () => {
    this.plotsController?.abort();
    this.setState({ genePanel: null });
  };

  getGeneRowKey = (row) => JSON.stringify([row.gene, row.treatment, row.SurgRad]);

  getKeys = () => {
    const { rows, resultType } = this.state;
    if (resultType === "cox") return coxKeys;
    const preferred = resultType === "treatment"
      ? ["gene", "treatment", "SurgRad", ...baseKeys.slice(1)] : baseKeys;
    const keys = [...new Set(rows.flatMap((row) => Object.keys(row)))];
    // Preserve additional projection fields returned by the API.
    return rows.length ? [...preferred.filter((key) => keys.includes(key)), ...keys.filter((key) => !preferred.includes(key))] : preferred;
  };

  getColumns = () => this.getKeys().map((key) => ({
    name: key === "pValue" && this.state.resultType === "cox" ? "Cox p-value" : labels[key] || key.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/_/g, " "),
    selector: (row) => (key === "confidenceInterval" ? row.ciLower : row[key]) ?? "", sortable: true,
    minWidth: key === "gene" || key === "treatment" ? "240px" : "150px",
    cell: (row) => key === "gene"
      ? <button type="button" className="btn btn-link p-0 text-start bmt-gene-link"
          onClick={() => this.handleGeneClick(row)}>{displayValue(row[key])}</button>
      : key === "estimate"
        ? <span style={{ color: estimateColor(row[key]) }}>{Number(row[key]) > 0 ? "+" : ""}{formatStat(row[key])}</span>
        : key === "SurgRad" ? displayValue(surgeryRadiationLabel(row[key]))
        : key === "confidenceInterval" ? confidenceInterval(row)
        : ["stdError", "statistic", "pValue", "hazardRatio", "logHazardRatio", "z"].includes(key)
          ? formatStat(row[key]) : displayValue(row[key]),
  }));

  exportCsv = () => {
    if (this.isExpired()) { this.expireAnalysis(); return; }
    const keys = this.getKeys();
    const quote = (value) => `"${displayValue(value).replace(/"/g, '""')}"`;
    const exportKeys = keys.flatMap((key) => key === "confidenceInterval" ? ["ciLower", "ciUpper"] : [key]);
    const csv = [exportKeys.map((key) => quote(key === "pValue" && this.state.resultType === "cox" ? "Cox p-value" : labels[key] || key)).join(","),
      ...this.getFilteredRows().map((row) => exportKeys.map((key) => quote(row[key])).join(","))].join("\r\n");
    saveAs(new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8" }), `projection_${this.state.resultType}.csv`);
  };

  getExpiryLabel = () => {
    const expires = Date.parse(this.state.expiresAt);
    if (!Number.isFinite(expires)) return "Expiry unavailable";
    const minutes = Math.ceil((expires - this.state.now) / 60000);
    return minutes <= 0 ? "Expired" : `${Math.floor(minutes / 60)}h ${minutes % 60}m remaining`;
  };

  render() {
    const { projection, resultType, loading, error, gene, treatment, SurgRad } = this.state;
    const rows = this.getFilteredRows();
    if (this.isExpired()) {
      return (
        <div className="bmt-page population-projection-results">
          <Link className="bmt-back-link" to="/app/populationProjection">
            <img src={backlogo} alt="" /> Back to projections
          </Link>
          <section className="bmt-results-card projection-expired-card" aria-labelledby="projection-expired-title" role="status">
            <div className="bmt-results-header">
              <div>
                <h1 id="projection-expired-title" className="bmt-results-title">Analysis expired</h1>
                <p className="bmt-results-subtitle">The 24-hour analysis window has ended.</p>
              </div>
              <span className="bmt-chip">Expired</span>
            </div>
            <div className="projection-expired-notice">
              Results and plots are no longer available. Upload the original CSV again to create a new analysis.
            </div>
            <p className="projection-expired-description">
              Previously downloaded files are not affected. This task can remain in your history for reference.
            </p>
            <Link className="btn btn-primary" to="/app/populationProjection/new">Create new projection</Link>
          </section>
        </div>
      );
    }
    const details = [
      ["Histological subtype", projection.histological_subtype],
      ["Molecular type", projection.molecular_type], ["Stage variables", projection.input_variables],
      ["Uploaded patients", projection.diagnostics?.input?.patientRows],
      ["Uploaded genes", projection.diagnostics?.input?.geneCount],
    ];
    return (
      <div className="bmt-page population-projection-results">
        <ToastContainer />
        <Link className="bmt-back-link" to="/app/populationProjection"><img src={backlogo} alt="" /> Back</Link>
        <div className="bmt-layout">
          <aside className="bmt-filter-col"><div className="bmt-filter-card">
            <div className="bmt-flow-badge">POPULATION PROJECTION</div>
            <div className="bmt-filter-title">Analysis details</div>
            <div className="bmt-filter-field"><span className="bmt-filter-label">Cancer type</span>
              <div className="bmt-static-field">{displayValue(projection.cancer_type)}</div></div>
            <dl className="projection-analysis-details">{details.map(([label, value]) => (
              <div key={label}><dt>{label}</dt><dd>{displayValue(value)}</dd></div>
            ))}</dl>
            <hr />
            <div className="bmt-filter-title">Result filters</div>
            <p className="text-muted small">Use the gene search to find a result.</p>
            {resultType === "treatment" && <>
              <div className="bmt-filter-field"><label className="bmt-filter-label" htmlFor="projection-treatment">Treatment</label>
                <select id="projection-treatment" className="form-select" value={treatment}
                  disabled={loading || !!error}
                  onChange={(event) => this.handleTreatmentChange(event.target.value)}>
                  <option value="">All Treatments</option>
                  {this.state.treatmentOptions.map((value) => <option key={value} value={value}>{value}</option>)}
                </select></div>
              <div className="bmt-filter-field"><label className="bmt-filter-label" htmlFor="projection-surgrad">Surgery / radiation</label>
                <select id="projection-surgrad" className="form-select" value={SurgRad}
                  disabled={loading || !!error}
                  onChange={(event) => this.handleSurgeryChange(event.target.value)}>
                  <option value="">All Groups</option>
                  {this.getSurgeryOptions().map((value) => <option key={value} value={value}>{surgeryRadiationLabel(value)}</option>)}
                </select></div>
            </>}
            <div className="projection-availability-card mb-3">
              <strong>Available for 24 hours</strong>
              <span>{this.getExpiryLabel()}</span>
            </div>
            {/* <Link className="bmt-btn-reset" to="/app/populationProjection/new">New projection</Link> */}
          </div></aside>

          <div className="bmt-results-col"><section className="bmt-results-card">
            <div className="bmt-results-header"><div>
              <h1 className="bmt-results-title">{projection.task_name || "Population projection"}</h1>
              <div className="bmt-results-subtitle">Population projection results</div>
            </div><button type="button" className="bmt-btn-download" onClick={this.exportCsv} disabled={loading || !rows.length}>
              <AiOutlineDownload size={16} /> Download CSV</button></div>
            <div className="bmt-chip-row">{[projection.histological_subtype, projection.molecular_type, projection.input_variables, projection.status]
              .filter(Boolean).map((value, index) => <span className="bmt-chip" key={index}>{value}</span>)}</div>
            <div className="projection-result-tabs" role="tablist" aria-label="Projection results">
              {tabs.map((tab) => <button key={tab.value} type="button" role="tab" id={`projection-tab-${tab.value}`}
                aria-selected={resultType === tab.value} aria-controls="projection-results-panel"
                onClick={() => this.handleTabChange(tab.value)}>
                {tab.label}
              </button>)}
            </div>
            <div role="tabpanel" id="projection-results-panel" aria-labelledby={`projection-tab-${resultType}`}>
              <form className="bmt-toolbar mb-3" onSubmit={this.handleSearch}>
                <div className="tp-search-wrap"><AiOutlineSearch className="tp-search-icon" size={14} />
                  <input aria-label="Search genes" placeholder="Search genes" value={gene}
                    onChange={(event) => this.setState({ gene: event.target.value })} /></div>
                <button type="submit" className="bmt-btn-reset">Search</button>
                <span className="tp-badge" role="status">{loading ? "Loading…" : `${rows.length} results`}</span>
              </form>
              <p className="text-muted small">{resultType === "prognostic" ? "Expression–survival associations across matched population stage groups."
                : resultType === "treatment" ? "Treatment associations across matched population stage groups." : "Cox / survival model results."}</p>
              {error && <div className="text-danger mb-3" role="alert">{error} <button type="button" className="bmt-btn-reset" onClick={this.fetchResults}>Retry</button></div>}
              <div className="bmt-table-wrap tp-data-table">
                <DataTable key={`${resultType}|${this.state.appliedGene}|${treatment}|${SurgRad}`}
                  columns={this.getColumns()} data={rows} customStyles={dataTableCustomStyles}
                  progressPending={loading} progressComponent={<div className="p-4" aria-label="Loading projection results"><Loader type="ball-pulse-rise" /></div>}
                  noDataComponent={<div className="p-4">There are no records to display</div>}
                  highlightOnHover fixedHeader fixedHeaderScrollHeight="58vh" sortIcon={<FiChevronDown size={12} />}
                  conditionalRowStyles={[{
                    when: (row) => !!this.state.genePanel && this.getGeneRowKey(row) === this.getGeneRowKey(this.state.genePanel.row),
                    style: { backgroundColor: "#eef0ff" },
                  }]}
                  pagination paginationPerPage={10} paginationComponentOptions={{ noRowsPerPage: true }} paginationComponent={RoundedPagination} />
              </div>
            </div>
          </section></div>
        </div>
        {this.state.genePanel && (
          <ProjectionGeneDetailPanel key={`${this.state.genePanel.resultType}|${this.getGeneRowKey(this.state.genePanel.row)}`}
            panel={this.state.genePanel} projection={projection} onClose={this.closeGenePanel}
            onRetry={() => this.handleGeneClick(this.state.genePanel.row)} />
        )}
      </div>
    );
  }
}
